import axios from 'axios';
import serverURL from '../config';

// Central axios instance used across the app
const apiClient = axios.create({
  baseURL: serverURL,
  timeout: 15000,
  withCredentials: true // important for httpOnly cookie auth
});

// --- Request logging (dev friendly) ---
apiClient.interceptors.request.use(
  (config) => {
    // Keep logs concise and useful
    try {
      const method = config.method ? config.method.toUpperCase() : 'GET';
      console.debug(`🔵 [REQUEST] ${method} ${config.url}`);
    } catch (e) {
      /* noop */
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Response handling / auth failure notifications ---
apiClient.interceptors.response.use(
  (response) => {
    try {
      console.debug(`✅ [RESPONSE] ${response.status} ${response.config.url}`);
    } catch (e) {}
    return response;
  },
  (error) => {
    // If there's no response it's likely network; let callers handle it
    if (!error.response) {
      console.error('❌ [ERROR] Network or CORS error', error.message);
      return Promise.reject(error);
    }

    const { status } = error.response;
    console.error(`❌ [ERROR] ${status} ${error.config?.url}`);

    if (status === 401) {
      // Notify app-wide listeners that authentication failed
      try {
        window.dispatchEvent(new CustomEvent('authError', { detail: { status: 401 } }));
      } catch (e) {}
    }

    return Promise.reject(error);
  }
);

// --- Simple request deduplication (coalesce identical in-flight requests) ---
const originalRequest = apiClient.request.bind(apiClient);
const inflight = new Map();

const genKey = (config) => {
  try {
    const method = (config.method || 'get').toLowerCase();
    const url = `${config.baseURL || ''}${config.url}`;
    const params = config.params ? JSON.stringify(config.params) : '';
    const data = config.data ? JSON.stringify(config.data) : '';
    return `${method}|${url}|${params}|${data}`;
  } catch (e) {
    return `${config.method || 'get'}|${config.url}`;
  }
};

apiClient.request = function (config) {
  const key = genKey(config);
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const promise = originalRequest(config).finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
};

export default apiClient;
